import {
  adminGroupName,
  MAIN_BRANCH,
  powerUserGroupName,
  advisorRoleName,
  studentRoleName,
  facultyRoleName,
  staffRoleName,
  medicalStudentRoleName,
  healthProfessionsStudentRoleName,
  medicalResidentRoleName,
  evmcRoleName,
  evmcFacultyRoleName,
  evmcStaffRoleName,
  evhscRoleName,
  evhscFacultyRoleName,
  evhscStaffRoleName,
  impersonationGroupName,
  samUserSuperGroupName,
  samUserPowerGroupName,
  samUserGroupName,
  atsUserSuperGroupName,
  atsUserPowerGroupName,
  atsUserGroupName,
} from '../common/constants';

/**
 * Returns loggedin user details or impersonated user details
 * @param user
 * @param impersonation
 * @returns {object}
 */
export const getUser = (user, impersonation) => {
  return impersonation.isImpersonating ? impersonation : user;
};

export const isProduction = () => {
  const isProduction = process.env.REACT_APP_GIT_BRANCH == MAIN_BRANCH;
  return isProduction;
};

/**
 * Returns true if the given user is an admin based on MIDAS/Shib group membership
 * @param user
 * @returns {boolean}
 */
export const isUserAdmin = (user) => {
  const isAdmin = adminGroupName in (user?.groups ?? {});
  return isAdmin;
};

/**
 * Returns true if the given user is a power user (content admin) based on MIDAS/Shib group membership
 * @param user
 * @returns {boolean}
 */
export const isUserPowerUser = (user) => {
  const isPowerUser = powerUserGroupName in (user?.groups ?? {});
  return isPowerUser;
};
/**
 * Returns true if the given user has impersonation privledges based on MIDAS/Shib group membership
 * @param user
 * @returns {boolean}
 */
export const isUserImpersonationUser = (user) => {
  const isImpersonationUser = impersonationGroupName in (user?.groups ?? {});
  return isImpersonationUser;
};

/**
 * Returns true if the given user is a student based on MIDAS/Shib roles
 * @param user
 * @returns {boolean}
 */
export const isUserStudent = (user) => {
  const isStudent = studentRoleName in (user?.roles ?? {});
  return isStudent;
};

/**
 * Returns true if the given user is faculty based on MIDAS/Shib roles
 * @param user
 * @returns {boolean}
 */
export const isUserFaculty = (user) => {
  const isFaculty = facultyRoleName in (user?.roles ?? {});
  return isFaculty;
};

/**
 * Returns true if the given user is staff based on MIDAS/Shib roles
 * @param user
 * @returns {boolean}
 */
export const isUserStaff = (user) => {
  const isStaff = staffRoleName in (user?.roles ?? {});
  return isStaff;
};

/**
 * Returns true if the given user is at least one of faculty or staff
 * @param user
 * @returns {boolean}
 */
export const isUserFacultyStaff = (user) => {
  return isUserFaculty(user) || isUserStaff(user);
};

/**
 * Returns true if the given user is at least one of faculty or staff
 * @param user
 * @returns {boolean}
 */
export const isUserAdvisor = (user) => {
  const isAdvisor = advisorRoleName in (user?.roles ?? {});
  return isAdvisor;
};

//isUserSAMSuperUser
//isUserSAMPowerUser
//isUSerSAMUser
//isUserSAM
/**
 * Returns true if the given user is SAM super user
 * @param user
 * @returns {boolean}
 */
export const isUserSAMSuperUser = (user) => {
  const isSamSuperUser = samUserSuperGroupName in (user?.groups ?? {});
  return isSamSuperUser;
};

/**
 * Returns true if the given user is SAM power user
 * @param user
 * @returns {boolean}
 */
export const isUserSAMPowerUser = (user) => {
  const isSamPowerUser = samUserPowerGroupName in (user?.groups ?? {});
  return isSamPowerUser;
};

/**
 * Returns true if the given user is SAM user
 * @param user
 * @returns {boolean}
 */
export const isUserSAMUser = (user) => {
  const isSamUser = samUserGroupName in (user?.groups ?? {});
  return isSamUser;
};

/**
 * Returns true if the given user is SAM user
 * @param user
 * @returns {boolean}
 */
export const isUserSAMPowerUp = (user) => {
  const isSamUser = isUserSAMPowerUser(user) || isUserSAMSuperUser(user);
  return isSamUser;
};

/**
 * Returns true if the given user is SAM
 * @param user
 * @returns {boolean}
 */
export const isUserSAM = (user) => {
  const isSam =
    isUserSAMSuperUser(user) ||
    isUserSAMPowerUser(user) ||
    isUserSAMUser(user) ||
    isUserAdvisor(user);
  return isSam;
};

/**
 * Returns true if the given user is ATS super user
 * @param user
 * @returns {boolean}
 */
export const isUserATSSuperUser = (user) => {
  const isAtsSuperUser = atsUserSuperGroupName in (user?.groups ?? {});
  return isAtsSuperUser;
};

/**
 * Returns true if the given user is ATS power user
 * @param user
 * @returns {boolean}
 */
export const isUserATSPowerUser = (user) => {
  const isAtsPowerUser = atsUserPowerGroupName in (user?.groups ?? {});
  return isAtsPowerUser;
};

/**
 * Returns true if the given user is ATS user
 * @param user
 * @returns {boolean}
 */
export const isUserATSUser = (user) => {
  const isAtsUser = atsUserGroupName in (user?.groups ?? {});
  return isAtsUser;
};

/**
 * Returns true if the given user is ATS
 * @param user
 * @returns {boolean}
 */
export const isUserATS = (user) => {
  const isAts = isUserATSSuperUser(user) || isUserATSPowerUser(user) || isUserATSUser(user);
  return isAts;
};

/**
 * Returns true if the given user is at least in one of the required groups
 * @param user
 * @param groups - Array of required groups
 * @returns {boolean}
 */
export const isUserInGroup = (user, groups) => {
  const isValid = groups.some((group) => group in (user?.groups ?? {}));
  return isValid;
};

/**
 * Returns true if the given user is a medical student based on MIDAS/Shib roles
 * @param user
 * @returns {boolean}
 */
export const isUserMedicalStudent = (user) => {
  const isMedicalStudent = medicalStudentRoleName in (user?.roles ?? {});
  return isMedicalStudent;
};

/**
 * Returns true if the given user is a health professions student based on MIDAS/Shib roles
 * @param user
 * @returns {boolean}
 */
export const isUserHealthProfessionsStudent = (user) => {
  const isHealthProfessionsStudent = healthProfessionsStudentRoleName in (user?.roles ?? {});
  return isHealthProfessionsStudent;
};

/**
 * Returns true if the given user is a health professions student based on MIDAS/Shib roles
 * @param user
 * @returns {boolean}
 */
export const isUserMedicalResident = (user) => {
  const isMedicalResident = medicalResidentRoleName in (user?.roles ?? {});
  return isMedicalResident;
};

/**
 * Returns true if the given user is evmc based on MIDAS/Shib roles
 * EVMC is specifically the campus located in Fort Norfolk
 * @param user
 * @returns {boolean}
 */
export const isUserEvmc = (user) => {
  const isEvmc = evmcRoleName in (user?.roles ?? {});
  return isEvmc;
};

/**
 * Returns true if the given user is evmc fac based on MIDAS/Shib roles
 * EVMC is specifically the campus located in Fort Norfolk
 * @param user
 * @returns {boolean}
 */
export const isUserEvmcFaculty = (user) => {
  const isEvmcFaculty = evmcFacultyRoleName in (user?.roles ?? {});
  return isEvmcFaculty;
};

/**
 * Returns true if the given user is evmc staff based on MIDAS/Shib roles
 * EVMC is specifically the campus located in Fort Norfolk
 * @param user
 * @returns {boolean}
 */
export const isUserEvmcStaff = (user) => {
  const isEvmcStaff = evmcStaffRoleName in (user?.roles ?? {});
  return isEvmcStaff;
};

/**
 * Returns true if the given user is evhsc based on MIDAS/Shib roles
 * @param user
 * @returns {boolean}
 */
export const isUserEvhsc = (user) => {
  const isEvhsc = evhscRoleName in (user?.roles ?? {});
  return isEvhsc;
};

/**
 * Returns true if the given user is evhsc fac based on MIDAS/Shib roles
 * @param user
 * @returns {boolean}
 */
export const isUserEvhscFaculty = (user) => {
  const isEvhscFaculty = evhscFacultyRoleName in (user?.roles ?? {});
  return isEvhscFaculty;
};

/**
 * Returns true if the given user is evhsc staff based on MIDAS/Shib roles
 * @param user
 * @returns {boolean}
 */
export const isUserEvhscStaff = (user) => {
  const isEvhscStaff = evhscStaffRoleName in (user?.roles ?? {});
  return isEvhscStaff;
};

/**
 * Returns true if the given user is from medical
 * @param user
 * @returns {boolean}
 */
export const isUserMedical = (user) => {
  const isMedical =
    isUserMedicalStudent(user) ||
    isUserHealthProfessionsStudent(user) ||
    isUserMedicalResident(user) ||
    isUserEvmc(user) ||
    isUserEvmcStaff(user) ||
    isUserEvmcFaculty(user) ||
    isUserEvhsc(user) ||
    isUserEvhscFaculty(user) ||
    isUserEvhscStaff(user);

  return isMedical;
};
